import { useEffect, useState, useMemo, useRef } from "react";
import { Nav, Navbar, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth, useUsersApi } from "utils/hooks";

import { jwtDecode } from "jwt-decode";
import { AvatarPicture } from "components";
import { Icons } from "components";
import { COLORS } from "utils/colors";

/**
 * Header component displays the top navigation bar with branding,
 * navigation links, and the user's email and avatar. It fetches
 * the user profile based on the tenant access token.
 *
 * @memberof modules:components/graphs
 * @returns {JSX.Element} The rendered header navigation bar
 */
const Header = () => {
  const {
    tenant_access_token,
    selected_tenant_id,
    userId,
    onLogout,
    onTenantLogout,
  } = useAuth();

  const api = useUsersApi();
  const navigate = useNavigate();

  /**
   * User profile object containing user details like email, first and last name.
   * @type {Object}
   */
  const [userProfile, setUserProfile] = useState({});

  /**
   * State to control visibility of a floating div (e.g., user details popup).
   * @type {boolean}
   */
  const [isFloatingDivVisible, setIsFloatingDivVisible] = useState(false);

  const floatingDivRef = useRef(null);
  const triggerRef = useRef(null);

  /**
   * Toggles visibility of the floating div when user email or avatar is clicked.
   */
  const handleEmailClick = () => {
    setIsFloatingDivVisible((prev) => !prev);
  };

  /**
   * Fetches the user profile asynchronously using the provided token.
   * Updates the userProfile state on success.
   *
   * @async
   * @param {string} token - The tenant access token for authentication
   * @returns {Promise<Object>} The user profile response
   * @throws Throws an error if the API call fails
   */
  const getUserProfile = async () => {
    try {
      const response = await api.getUserProfile(userId);
      setUserProfile(response?.data);
      return response;
    } catch (e) {
      //alert(e);
      throw e;
    }
  };

  /**
   * Effect hook that runs once on component mount to fetch user profile
   * using the tenant access token.
   */
  useEffect(() => {
    getUserProfile();
  }, []);

  // Memoize decoded tenant info to avoid recalculation
  const activeTenantName = useMemo(() => {
    if (tenant_access_token) {
      try {
        return jwtDecode(tenant_access_token).active_tenant_name;
      } catch {
        return null;
      }
    }
    return null;
  }, [tenant_access_token]);

  // 👇 Hide floating div when clicking outside it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        floatingDivRef.current &&
        !floatingDivRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsFloatingDivVisible(false);
      }
    };

    if (isFloatingDivVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFloatingDivVisible]);

  return (
    <>
      <div
        className="fixed-top"
        style={{
          minWidth: 900,
          position: "sticky",
          backgroundColor: "#db6930",
          color: "white",
        }}
      >
        <Navbar bg="dark" variant="dark" className="m-0 p-0">
          <div
            style={{
              display: "flex",
              width: 250,
              justifyContent: "center",
              padding: 8,
            }}
          >
            <Navbar.Brand href="/"> {window.RUNTIME_CONFIG?.APP?.TITLE }</Navbar.Brand>
          </div>
          <Nav className="me-auto m-0 p-0">
            <Nav.Link href="/">Home</Nav.Link>
            <Nav.Link href="https://roinspace.com">About</Nav.Link>
          </Nav>

          <div
            ref={triggerRef}
            className="hoverable-div"
            onClick={handleEmailClick}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 15,
                justifyContent: "right",
                textAlign: "right",
                marginRight: 15,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <small>{userProfile.email_address}</small>
              {activeTenantName && <small>{activeTenantName}</small>}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "right",
                alignItems: "center",
                marginRight: 15,
              }}
              onClick={handleEmailClick}
            >
              <AvatarPicture
                width={28}
                height={28}
                first_name={userProfile.first_name}
                last_name={userProfile.last_name}
              />
            </div>
          </div>
        </Navbar>
      </div>
      {isFloatingDivVisible && (
        <div
          ref={floatingDivRef}
          style={{
            position: "absolute",
            right: 7,
            backgroundColor: COLORS.ligthGrey,
            color: COLORS.greyFontColor,
            boxShadow: "0px 2px 8px rgba(24, 15, 15, 0.3)",

            padding: "10px 15px 0px 15px",
            zIndex: 1000,
            minWidth: 200,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          <Row className="p-0 m-0 mb-4">
            <Col className="p-0 m-0">
              {activeTenantName && (
                <>
                  <small>{activeTenantName}</small>
                </>
              )}
            </Col>
          </Row>
          <Row className="p-0 m-0">
            <Col className="p-0 m-0">
              <AvatarPicture
                width={120}
                height={120}
                first_name={userProfile.first_name}
                last_name={userProfile.last_name}
              />
            </Col>
            <Col className="p-0 m-0">
              <Row className="p-0 m-0">
                {!userProfile.first_name || !userProfile.last_name ? (
                  <small>{userProfile.email_address}</small>
                ) : (
                  <strong>
                    {userProfile.first_name} {userProfile.last_name}
                  </strong>
                )}
              </Row>
              <Row className="p-0 m-0">
                <small>{userProfile.email_address}</small>
              </Row>
              <Row className="p-0 m-0">
                <small>
                  {" "}
                  <a href="/settings/profile">View profile</a>
                </small>
              </Row>
              <Row className="p-0 m-0">
                <small className="pt-0">
                  {" "}
                  <a href="/" onClick={() => onTenantLogout()}>
                    Switch tenant
                  </a>
                </small>
              </Row>
            </Col>
          </Row>
          <Row className="p-0 mb-0 mt-2 text-end hoverable-div">
            <Nav.Link
              eventKey={`logout`}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                padding: 10,
                color: "red",
              }}
              onClick={(e) => {
                onLogout();
              }}
            >
              <Icons
                name={"logout-icon"}
                color={"#bf1f27"}
                width={20}
                height={20}
              />

              {"Logout"}
            </Nav.Link>
          </Row>
        </div>
      )}
    </>
  );
};

export default Header;
